import { Check } from 'lucide-react'

import { cn } from '../lib/utils'
import { Chip } from './chip'

export interface DayPickerDay {
  value: string
  /** Shown in the circle, e.g. "M". */
  initial: string
  /** Accessible name, e.g. "Monday". */
  label: string
}

export interface DayPickerGroup {
  value: string
  label: string
  days: readonly string[]
}

export interface DayPickerProps {
  days: readonly DayPickerDay[]
  selected: readonly string[]
  /** Fired with one day, or with every day of a group chip. Adding and removing is the caller's call. */
  onToggle: (days: readonly string[]) => void
  /** Accessible name for the whole picker, already translated. */
  label: string
  /** Shortcut chips, e.g. Weekdays / Weekend. Active when every day in the group is selected. */
  groups?: readonly DayPickerGroup[]
  className?: string
}

/** Seven day toggles plus group shortcut chips — picks which days a rule applies to. */
export function DayPicker({ days, selected, onToggle, label, groups = [], className }: DayPickerProps) {
  return (
    <fieldset
      aria-label={label}
      className={cn('m-0 flex min-w-0 flex-col gap-2 border-0 p-0 lg:flex-row lg:items-center lg:gap-3', className)}>
      <div className="flex gap-1">
        {days.map((day) => {
          const on = selected.includes(day.value)
          return (
            <button
              key={day.value}
              type="button"
              aria-pressed={on}
              aria-label={day.label}
              onClick={() => onToggle([day.value])}
              className="group relative flex h-11 w-10 items-center justify-center rounded-xl text-[0.8125rem] font-semibold text-muted-foreground focus-visible:outline focus-visible:outline-[3px] focus-visible:-outline-offset-2 focus-visible:outline-pelorous-500 dark:focus-visible:outline-pelorous-300">
              <span
                className={cn(
                  'flex size-[2.125rem] items-center justify-center rounded-full transition-[background-color,color,box-shadow] duration-150 motion-reduce:transition-none',
                  on ? 'bg-brand-green-fill text-white' : 'ring-1 ring-inset ring-border group-hover:ring-brand-green-border'
                )}>
                {day.initial}
              </span>
              {on && (
                <span
                  aria-hidden="true"
                  className="absolute right-px top-0.5 flex size-3.5 items-center justify-center rounded-full bg-card text-brand-green-on-tint ring-1 ring-border">
                  <Check className="size-2.5" strokeWidth={3} />
                </span>
              )}
            </button>
          )
        })}
      </div>
      {groups.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {groups.map((group) => (
            <Chip
              key={group.value}
              variant="selectable"
              active={group.days.every((d) => selected.includes(d))}
              onClick={() => onToggle(group.days)}>
              {group.label}
            </Chip>
          ))}
        </div>
      )}
    </fieldset>
  )
}
